<template>
    <v-container fill-height fluid class="mob-page pa-0">
        <v-row no-gutters class="fill-height pt-1 mob-layout">
            <v-row no-gutters align="center" justify="center" class="mob-header">
                <v-col cols="12" class="text-center">
                    <h3 class="mob-page-title text--white80">MAN OVER BOARD</h3>
                </v-col>
            </v-row>
            <v-row no-gutters class="py-1 mob-toolbar">
                <v-col cols="6" class="mob-phone-column">
                    <vue-tel-input v-model="vueTel.phone"
                                   class="tel-input--blue tel-input--toolbar"
                                   v-bind="vueTel.props"
                                   ref="phoneInputRef"
                                   @validate="phoneObject"></vue-tel-input>
                </v-col>
                <v-col cols="2" class="mob-toolbar-column">
                    <v-btn small class="mob-toolbar-button" color="white" @click="showContactDialog">VIP</v-btn>
                </v-col>
                <v-col cols="4" class="mob-toolbar-column">
                    <v-btn small
                           class="mob-toolbar-button mob-send-button"
                           color="white"
                           @click="sendMobMessage"
                           :disabled="!isValid && !isFetchingPosition"
                           :loading="isFetchingPosition">
                        <span class="mob-send-button-label">Send<br />Message</span>
                    </v-btn>
                </v-col>
            </v-row>
            <v-row no-gutters align="center" justify="center" class="pt-1 mob-coordinates">
                <v-col cols="12" class="text-center">
                    <span class="white70--text caption metric">Latitude: {{ latitude }}&deg;{{ latLetter }}</span>
                    <span class="white70--text caption sep" aria-hidden="true">|</span>
                    <span class="white70--text caption metric">Longitude: {{ longitude }}&deg;{{ lngLetter }}</span>
                </v-col>
            </v-row>
            <v-col cols="12" class="fill-height mob-map-col">
                <div class="map mob-map" ref="map"></div>
            </v-col>
        </v-row>

        <LoadingDialog v-model="isFetchingPosition"
                       message="OBTAINING POSITION ..." />

        <v-dialog v-if="showDialog && !showAddContactDialog && !showEditContactDialog"
                  v-model="showDialog"
                  persistent
                  max-width="520"
                  content-class="mob-vip-dialog-overlay">
                  <v-card class="backgroundWhite mob-vip-dialog">
                      <MobVipDialogHeader
                          eyebrow="SAFETY CONTACTS"
                          :title="VIPcontacts.length > 0 ? 'Select a VIP contact' : 'No VIP contacts available'"
                          close-label="Close VIP contacts"
                          @close="closeContactDialog"
                      />
                <v-card-text class="mob-vip-dialog-content">
                    <v-list v-if="VIPcontacts.length > 0" class="mob-vip-list" two-line>
                        <v-list-item
                            v-for="item in VIPcontacts"
                            :key="item.id || item.phoneNumber || item.name"
                            class="mob-vip-contact"
                            @click="selectContact(item)"
                        >
                            <v-list-item-content>
                                <v-list-item-title class="font-weight-bold">{{ item.name }}</v-list-item-title>
                                <v-list-item-subtitle>
                                    <v-icon small>mdi-phone</v-icon>
                                    {{ item.phoneNumber }}
                                </v-list-item-subtitle>
                            </v-list-item-content>
                            <div class="mob-vip-contact-actions">
                                <v-btn icon small :aria-label="`Edit ${item.name}`" @click.stop="editVIPContact(item)">
                                    <v-icon small color="orange">mdi-pencil</v-icon>
                                </v-btn>
                                <v-btn icon small :aria-label="`Delete ${item.name}`" @click.stop="deleteVIPContact(item)">
                                    <v-icon small color="error">mdi-delete</v-icon>
                                </v-btn>
                            </div>
                        </v-list-item>
                    </v-list>
                    <div v-else class="mob-vip-empty">
                        <v-icon color="grey">mdi-account-off-outline</v-icon>
                        <span>No VIP contacts found</span>
                    </div>
                </v-card-text>
                <v-card-actions class="mob-dialog-actions">
                    <v-btn small text color="primary" @click="closeContactDialog">Back</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn v-if="VIPcontacts.length < 10"
                           small
                           color="primary"
                           @click="openAddContactDialog">
                        <v-icon left small>mdi-account-plus</v-icon>
                        Add VIP contact
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <AddVipContactDialog
            v-model="showAddContactDialog"
            @created="handleVipContactCreated"
        />
        <EditVipContactDialog
            v-model="showEditContactDialog"
            :contact="editingContact"
            @updated="handleVipContactUpdated"
        />
    </v-container>
</template>

<script lang="ts">
    import {
        defineComponent,
        getCurrentInstance,
        ref,
        computed,
        onMounted,
        onBeforeUnmount,
        nextTick,
    } from "vue";
    import useMap from "@/utilities/useGoogleMaps";
    import {
        getGeolocationPermissionState,
        MANUAL_GEOLOCATION_OPTIONS,
        TRACKING_GEOLOCATION_OPTIONS,
        getGeolocationErrorMessage,
        getGeolocationUnavailableMessage,
        isGeolocationPermissionDenied,
        requestCurrentPosition,
        resolveGeolocationAccess,
        startWatchingPosition,
        stopWatchingPosition,
    } from "@/utilities/useGeolocation";
    import { mobState, app, alarm } from "@/store/index";
    import useVIPContacts from "@/store/useVIPContacts";
    import VIPContactViewModel from "@/models/VIPContactViewModel";
    import { VueTelInput } from "vue-tel-input";
    import useVIPContact from "../store/useVIPContact";
    import moment from "moment-timezone"; // ⬅️ add

    export default defineComponent({
        components: {
            VueTelInput,
            AddVipContactDialog: () =>
                import(/* webpackChunkName: "settings" */ "./components/Dialogs/AddVipContactDialog.vue"),
            EditVipContactDialog: () =>
                import(/* webpackChunkName: "settings" */ "./components/Dialogs/EditVipContactDialog.vue"),
            MobVipDialogHeader: () =>
                import(/* webpackChunkName: "settings" */ "./components/Dialogs/MobVipDialogHeader.vue"),
            LoadingDialog: () =>
                import(/* webpackChunkName: "settings" */ "./components/Dialogs/LoadingDialog.vue"),
        },
        setup() {
            const root = getCurrentInstance()?.proxy as any;
            const GOOGLE_MAP_ID = process.env.VUE_APP_GOOGLE_MAPS_MAP_ID || "DEMO_MAP_ID";
            const { VIPcontacts, getVIPContacts } = useVIPContacts();
            const { remove } = useVIPContact();
            const map = ref();
            const { googleMapApi } = useMap();
            const location = ref<GeolocationPosition>();
            type AdvancedMarker = google.maps.marker.AdvancedMarkerElement;
            type ManagedAdvancedMarker = AdvancedMarker & {
                __gmpClickHandler?: EventListener;
            };
            type ImageMarkerConfig = {
                url: string;
                width: number;
                height: number;
                alt: string;
            };
            let myGoogleMap: google.maps.Map;
            let userMarker: AdvancedMarker;
            let infoWindow: google.maps.InfoWindow;
            let user: google.maps.LatLng;
            const phoneInputRef = ref();
            const phone = ref<string>('');
            const showDialog = ref<boolean>(false);
            const showAddContactDialog = ref<boolean>(false);
            const showEditContactDialog = ref<boolean>(false);
            const editingContact = ref<VIPContactViewModel | null>(null);
            const errorMessage = ref<string>("");
            const isFetchingPosition = ref<boolean>(true);
            let userPositionWatchId: number | null = null;

            const warnMob = (message: string, extra?: unknown) => {
                console.warn(`[MobView] ${message}`, extra ?? "");
            };

            const sleep = (timeoutMs: number) =>
                new Promise((resolve) => window.setTimeout(resolve, timeoutMs));

            const resolveMapElement = async (): Promise<HTMLElement | undefined> => {
                for (let attempt = 1; attempt <= 10; attempt += 1) {
                    await nextTick();

                    const templateRef = map.value;
                    const rootRef = (root?.$refs?.map ?? undefined) as unknown;
                    const rootElement = root?.$el instanceof Element
                        ? (root.$el.querySelector(".map") as HTMLElement | null)
                        : null;
                    const documentElement = document.querySelector(".map") as HTMLElement | null;

                    const mapElement = templateRef instanceof HTMLElement
                        ? templateRef
                        : rootRef instanceof HTMLElement
                            ? rootRef
                            : rootElement instanceof HTMLElement
                                ? rootElement
                                : documentElement instanceof HTMLElement
                                    ? documentElement
                                    : undefined;

                    if (mapElement?.isConnected) {
                        return mapElement;
                    }

                    await sleep(100);
                }

                warnMob("map initialization skipped because map container ref is unavailable", {
                    templateRef: map.value,
                    rootRef: root?.$refs?.map,
                });
                return undefined;
            };

            // === helpers for pretty InfoWindows with local time ===
            const userTZ = ref<string>(
                Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
            );
            const formatLocal = (ts: number | string, fmt = "YYYY-MM-DD HH:mm:ss z") =>
                (typeof ts === "number" ? moment(ts) : moment.utc(ts))
                    .tz(userTZ.value)
                    .format(fmt);

            const infoCard = (title: string, rows: Array<[string, string | number]>) => `
        <div style="
          font-family: system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;
          color:#fff; background:#0b132b; border-radius:12px;
          min-width:260px; max-width:340px; overflow:hidden;">
          <div style="padding:10px 36px 10px 12px; font-weight:600; font-size:var(--app-font-body);">
            ${title}
          </div>
          <div style="padding:10px 12px; background:rgba(255,255,255,.04)">
            ${rows.map(([k, v]) => `
              <div style="display:flex; justify-content:space-between; gap:12px; padding:6px 0; border-bottom:1px solid rgba(255,255,255,.08)">
                <div style="opacity:.85">${k}</div>
                <div style="font-weight:600">${v}</div>
              </div>
            `).join("")}
          </div>
        </div>
      `;

            const createImageMarkerContent = (icon: ImageMarkerConfig): HTMLDivElement => {
                const wrapper = document.createElement("div");
                wrapper.style.width = `${icon.width}px`;
                wrapper.style.height = `${icon.height}px`;
                wrapper.style.display = "flex";
                wrapper.style.alignItems = "center";
                wrapper.style.justifyContent = "center";

                const img = document.createElement("img");
                img.src = icon.url;
                img.alt = icon.alt;
                img.width = icon.width;
                img.height = icon.height;
                img.draggable = false;
                img.style.width = "100%";
                img.style.height = "100%";
                img.style.objectFit = "contain";
                img.style.pointerEvents = "none";
                img.style.userSelect = "none";

                wrapper.appendChild(img);
                return wrapper;
            };

            const createAdvancedMarker = (options: {
                position: google.maps.LatLng | google.maps.LatLngLiteral;
                icon: ImageMarkerConfig;
                title: string;
                visible?: boolean;
                clickable?: boolean;
            }): AdvancedMarker => {
                return new google.maps.marker.AdvancedMarkerElement({
                    map: options.visible === false ? null : myGoogleMap,
                    position: options.position,
                    content: createImageMarkerContent(options.icon),
                    title: options.title,
                    gmpClickable: options.clickable ?? false,
                });
            };

            const setMarkerVisible = (marker: AdvancedMarker, visible: boolean) => {
                marker.map = visible ? myGoogleMap : null;
            };

            const setMarkerPosition = (
                marker: AdvancedMarker,
                position: google.maps.LatLng | google.maps.LatLngLiteral
            ) => {
                marker.position = position;
            };

            const getMarkerPosition = (marker: AdvancedMarker): google.maps.LatLng => {
                if (marker.position instanceof google.maps.LatLng) {
                    return marker.position;
                }

                if (marker.position) {
                    return new google.maps.LatLng(marker.position.lat, marker.position.lng);
                }

                throw new Error("Marker position is not set.");
            };

            const openInfoWindowAtMarker = (
                currentInfoWindow: google.maps.InfoWindow,
                marker: AdvancedMarker
            ) => {
                currentInfoWindow.open({
                    map: myGoogleMap,
                    anchor: marker,
                    shouldFocus: false,
                });
            };

            const setMarkerClickHandler = (
                marker: AdvancedMarker,
                handler: () => void
            ) => {
                const managedMarker = marker as ManagedAdvancedMarker;
                if (managedMarker.__gmpClickHandler) {
                    managedMarker.removeEventListener("gmp-click", managedMarker.__gmpClickHandler);
                }

                const listener: EventListener = () => handler();
                managedMarker.__gmpClickHandler = listener;
                managedMarker.addEventListener("gmp-click", listener);
            };

            const user_lat = computed<number>(() => { return alarm.user_lat ? alarm.user_lat : 0; });
            const user_lng = computed<number>(() => { return alarm.user_lng ? alarm.user_lng : 0; });

            const latitude = computed<string>(() => {
                return user_lat.value != 0 ? user_lat.value.toFixed(6) : "-.--";
            });

            const longitude = computed<string>(() => {
                return user_lng.value != 0 ? user_lng.value.toFixed(6) : "-.--";
            });

            const latLetter = computed<string>(() => user_lat.value >= 0 ? "N" : "S");
            const lngLetter = computed<string>(() => user_lng.value >= 0 ? "E" : "W");

            const selectContact = (VIPcontact: VIPContactViewModel) => {
                vueTel.value.phone = VIPcontact.phoneNumber;
                closeContactDialog();
            };

            const showContactDialog = () => { showDialog.value = true; };
            const closeContactDialog = () => { showDialog.value = false; };
            const openAddContactDialog = () => { showAddContactDialog.value = true; };

            const handleVipContactCreated = async () => {
                if (!await getVIPContacts()) {
                    app.error = "VIP contact was added, but the list could not be refreshed.";
                }
            };

            const handleVipContactUpdated = async () => {
                if (!await getVIPContacts()) {
                    app.error = "VIP contact was updated, but the list could not be refreshed.";
                }
            };

            const editVIPContact = (VIPcontact: VIPContactViewModel) => {
                editingContact.value = VIPcontact;
                showEditContactDialog.value = true;
            };

            const deleteVIPContact = (VIPcontact: VIPContactViewModel) => {
                remove(VIPcontact.id).then(() => {
                    getVIPContacts();
                });
            };

            const requestUserPosition = async (
                options: PositionOptions = MANUAL_GEOLOCATION_OPTIONS
            ) => {
                try {
                    const position = await requestCurrentPosition(options);
                    handlePositionSuccess(position);
                } catch (error) {
                    if (error instanceof Error) {
                        isFetchingPosition.value = false;
                        errorMessage.value = error.message || getGeolocationUnavailableMessage();
                        app.error = errorMessage.value;
                        return;
                    }

                    handlePositionError(error as GeolocationPositionError);
                }
            };

            const handlePositionSuccess = (position: GeolocationPosition) => {
                if (typeof google === "undefined" || !google.maps) {
                    warnMob("handlePositionSuccess skipped because Google Maps is unavailable");
                    return;
                }

                alarm.setUserLocation(position);
                location.value = position;

                user = new google.maps.LatLng(position.coords.latitude, position.coords.longitude);

                if (userMarker) {
                    setMarkerPosition(userMarker, user);
                    setMarkerVisible(userMarker, true);
                } else {
                    warnMob("user marker is not initialized yet");
                }

                // ✅ pretty card + local time
                const rowsUser: Array<[string, string | number]> = [
                    ["Local time", formatLocal(position.timestamp)],
                    ["Latitude", user.lat().toFixed(6)],
                    ["Longitude", user.lng().toFixed(6)],
                ];
                if (Number.isFinite(position.coords.accuracy)) {
                    rowsUser.push(["Accuracy (m)", Math.round(position.coords.accuracy)]);
                }
                const contentUser = infoCard("User position", rowsUser);

                if (userMarker && infoWindow) {
                    setMarkerClickHandler(userMarker, () => {
                        infoWindow.close();
                        infoWindow.setContent(contentUser);
                        openInfoWindowAtMarker(infoWindow, userMarker);
                    });
                }

                isFetchingPosition.value = false;
                if (myGoogleMap) {
                    myGoogleMap.setCenter(user);
                }
            };

            const goToGeoPerm = () => {
                root.$router.push({ name: "geo-permission", query: { destination: "mob" } }).catch(() => undefined);
            };

            const handlePositionError = (error: GeolocationPositionError) => {
                errorMessage.value = getGeolocationErrorMessage(error);
                isFetchingPosition.value = false;
                app.error = errorMessage.value;

                if (isGeolocationPermissionDenied(error)) {
                    goToGeoPerm();
                    return;
                }

                warnMob("mob user position watch failed", {
                    code: error.code,
                    message: error.message,
                });
            };

            const sendMobMessage = () => {
                if (latitude.value && longitude.value) {
                    const phoneData = phoneInputRef.value;
                    const number = phoneData.phoneObject ? phoneData.phoneObject.number : phone.value;
                    mobState
                        .sendMobMessage({
                            lat: latitude.value,
                            lng: longitude.value,
                            phone: number
                        })
                        .then(() => {
                            app.success = 'MOB message sent successfully.';
                        });
                }
            };

            const vueTel = ref({
                phone: '',
                props: {
                    autoFormat: true,
                    autoDefaultCountry: true,
                    dropdownOptions: {
                        showDialCodeInList: true,
                        showDialCodeInSelection: false,
                        showFlags: true,
                        showSearchBox: true,
                        width: '300px',
                    },
                    ignoredCountries: ['KP', 'RU'],
                    inputOptions: {
                        autocomplete: 'on',
                        showDialCode: true,
                        placeholder: 'Enter phone number',
                        required: true,
                    },
                    mode: 'international',
                    preferredCountries: ['DK', 'NO', 'SE'],
                    validCharactersOnly: true,
                },
            });

            onMounted(async () => {
                await nextTick();

                const accessResolution = await resolveGeolocationAccess(MANUAL_GEOLOCATION_OPTIONS);
                console.info("[GeoTrace] mob.onMounted resolved permission state", {
                    permissionState: accessResolution.permissionState,
                    accessStatus: accessResolution.accessStatus,
                });

                if (accessResolution.accessStatus !== "granted") {
                    errorMessage.value = accessResolution.accessStatus === "denied"
                        ? "User denied the request for geolocation."
                        : getGeolocationUnavailableMessage();
                    isFetchingPosition.value = false;
                    app.error = errorMessage.value;
                    goToGeoPerm();
                    return;
                }

                googleMapApi.load().then(() => {
                    console.log("Google Maps API loaded");
                    if (typeof google === "undefined" || !google.maps) {
                        warnMob("Google Maps API reported loaded, but google.maps is unavailable");
                        return;
                    }

                    resolveMapElement().then((mapElement) => {
                        if (!mapElement) {
                            return;
                        }

                        const userPos = new google.maps.LatLng(user_lat.value, user_lng.value);
                        infoWindow = new google.maps.InfoWindow();
                        const userIcon: ImageMarkerConfig = {
                            url: 'https://dsspoc.azurewebsites.net/img/icons/map-marker-user.svg',
                            width: 80,
                            height: 80,
                            alt: 'You are here.',
                        };

                        myGoogleMap = new google.maps.Map(mapElement, {
                            center: userPos,
                            mapTypeId: "satellite",
                            zoom: 16,
                            disableDefaultUI: true,
                            backgroundColor: "#000000",
                            zoomControl: false,
                            mapTypeControl: true,
                            mapTypeControlOptions: {
                                style: google.maps.MapTypeControlStyle.DROPDOWN_MENU
                            },
                            mapId: GOOGLE_MAP_ID,
                            fullscreenControl: true
                        });

                        userMarker = createAdvancedMarker({
                            position: userPos,
                            icon: userIcon,
                            title: "You are here.",
                            clickable: true,
                        });

                        myGoogleMap.setCenter(getMarkerPosition(userMarker));

                        if (accessResolution.position) {
                            handlePositionSuccess(accessResolution.position);
                        } else {
                            requestUserPosition(MANUAL_GEOLOCATION_OPTIONS);
                        }
                        if (userPositionWatchId === null) {
                            userPositionWatchId = startWatchingPosition(
                                (position: GeolocationPosition) => {
                                    handlePositionSuccess(position);
                                },
                                (error: GeolocationPositionError) => {
                                    handlePositionError(error);
                                },
                                TRACKING_GEOLOCATION_OPTIONS
                            );
                        }
                    }).catch((error) => {
                        warnMob("MOB map element resolution failed", error);
                    });
                }).catch((error) => {
                    warnMob("Google Maps API failed to load", error);
                });
            });

            onBeforeUnmount(() => {
                stopWatchingPosition(userPositionWatchId);
                userPositionWatchId = null;
            });

            return {
                phone,
                sendMobMessage,
                phoneInputRef,
                map,
                latitude,
                longitude,
                latLetter,
                lngLetter,
                showContactDialog,
                showDialog,
                closeContactDialog,
                VIPcontacts,
                selectContact,
                vueTel,
                openAddContactDialog,
                handleVipContactCreated,
                handleVipContactUpdated,
                showAddContactDialog,
                deleteVIPContact,
                showEditContactDialog,
                editVIPContact,
                editingContact,
                requestUserPosition,
                isFetchingPosition,
            };
        },
        data: () => ({
            isValid: false,
        }),
        methods: {
            phoneObject: function (object) {
                this.isValid = object.valid === true;
            },
        }
    });
</script>

<style lang="scss" scoped>
    .mob-page {
        height: 100%;
        overflow: hidden;
    }

    .mob-layout {
        height: 100%;
        flex-direction: column;
        flex-wrap: nowrap;
    }

    .mob-header,
    .mob-toolbar,
    .mob-coordinates {
        position: relative;
        z-index: 2;
        flex: 0 0 auto;
    }

    .mob-page-title {
        margin: 0;
        padding: 5px 0;
        font-size: var(--app-font-body);
        line-height: 1.2;
        letter-spacing: 0.04em;
    }

    .mob-toolbar {
        width: 100%;
        max-width: 680px;
        align-items: center;
        margin-right: auto;
        margin-left: auto;
        padding: 4px 8px !important;
        z-index: 3;
    }

    .mob-toolbar .mob-phone-column {
        padding: 0 4px 0 0 !important;
    }

    .mob-toolbar .mob-toolbar-column {
        padding: 0 4px !important;
    }

    .mob-toolbar-button {
        width: 100%;
        min-width: 0 !important;
        max-width: 100%;
        min-height: 36px !important;
        padding: 0 4px !important;
        font-size: var(--app-font-small);
        white-space: normal;
    }

    .mob-send-button {
        border: 1px solid rgba(255, 255, 255, 0.8) !important;
        background: #ffffff !important;
        color: var(--app-color-navy-blue) !important;
        line-height: 1.05;
    }

    .mob-send-button-label {
        display: block;
        line-height: 1.05;
        text-align: center;
    }

    .mob-send-button.v-btn--disabled {
        border-color: rgba(255, 255, 255, 0.55) !important;
        background: rgba(255, 255, 255, 0.14) !important;
        color: rgba(255, 255, 255, 0.75) !important;
        opacity: 1;
    }

    .mob-coordinates {
        width: 100%;
        max-width: 720px;
        margin-right: auto;
        margin-left: auto;
        padding: 2px 6px 4px !important;
    }

    .mob-coordinates .metric {
        font-size: var(--app-font-caption);
        white-space: nowrap;
    }

    .mob-coordinates .sep {
        margin: 0 4px;
    }

    .mob-map-col {
        position: relative;
        flex: 1 1 auto;
        min-height: 0;
        overflow: hidden;
        z-index: 1;
    }

    .mob-map {
        position: relative;
        width: 100%;
        height: 100%;
        overflow: hidden;
    }

    .mob-vip-dialog-content {
        padding: 12px 16px !important;
    }

    .mob-vip-list {
        padding: 0;
    }

    .mob-vip-contact {
        min-height: 60px;
        padding: 0 8px;
        border-bottom: 1px solid #e2e8f0;
    }

    .mob-vip-contact-actions {
        display: flex;
        flex: 0 0 auto;
        gap: 2px;
    }

    .mob-vip-empty {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        min-height: 72px;
        color: #64748b;
        font-size: var(--app-font-body);
    }

    .mob-dialog-actions {
        gap: 8px;
    }

    .mob-dialog-actions .v-btn {
        min-width: 0;
        padding: 0 8px;
    }

    /* Remove Google InfoWindow’s default white bubble/chrome */
    ::v-deep .gm-style .gm-style-iw-c {
        background: transparent !important;
        padding: 0 !important;
        box-shadow: none !important;
        border-radius: 12px !important; /* matches your card */
    }

    ::v-deep .gm-style .gm-style-iw-d {
        overflow: visible !important; /* avoids inner scrollbars cutting edges */
        padding: 0 !important;
        background: transparent !important;
    }

    /* Hide the little white pointer (“tail”). Comment out if you want to keep it */
    ::v-deep .gm-style .gm-style-iw-t::after,
    ::v-deep .gm-style .gm-style-iw-t::before {
        display: none !important;
    }

    /* Nudge the default InfoWindow close button */
    ::v-deep .gm-style .gm-ui-hover-effect {
        top: 34px !important; /* push it DOWN a touch */
        right: 0px !important; /* move it a bit further RIGHT (closer to edge) */
        width: 28px !important;
        height: 28px !important;
        background: transparent !important;
        border-radius: 8px !important;
        box-shadow: none !important;
    }

    /* Keep the icon centered and white */
    ::v-deep .gm-style .gm-ui-hover-effect span {
        margin: 0 !important;
        width: 18px !important;
        height: 18px !important;
        background-color: #fff !important; /* the “X” color */
        opacity: 0.95;
    }

    ::v-deep .gm-style .gm-ui-hover-effect img {
        width: 16px !important;
        height: 16px !important;
        filter: invert(1) brightness(2); /* make it white for dark header */
        opacity: 0.9;
    }

    .sep {
        display: inline-block;
        margin: 0 12px; /* tweak to taste */
        opacity: 0.7; /* optional: softer divider */
    }

</style>